import React, { useState } from "react";
import { IoTrashOutline } from "react-icons/io5";
import { LiaUserSlashSolid } from "react-icons/lia";
import ConfirmationModal from "../../../ui/ConfirmationModal";

interface BookingRow {
  id: number;
  reserved_at: string;
  reserved_time: string;
  seats: number;
  mycia_commission: number;
}

interface Props {
  rows: BookingRow[];
  onUpdateStatus?: (booking_id: number, status: 9 | 17) => void;
  isUpdating?: boolean;
}

const BookingDetailTable: React.FC<Props> = ({ rows, onUpdateStatus, isUpdating }) => {
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    booking: BookingRow | null;
    status: 9 | 17 | null;
  }>({
    isOpen: false,
    booking: null,
    status: null,
  });

  if (!rows || rows.length === 0) {
    return <p className="text-xs text-gray-500">Nessuna transazione trovata.</p>;
  }

  const handleUpdateStatusClick = (booking: BookingRow, status: 9 | 17) => {
    if (onUpdateStatus && !isUpdating) {
      setConfirmModal({
        isOpen: true,
        booking: booking,
        status: status,
      });
    }
  };

  const handleConfirm = () => {
    if (onUpdateStatus && confirmModal.booking !== null && confirmModal.status !== null) {
      onUpdateStatus(confirmModal.booking.id, confirmModal.status);
    }
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCancel = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border rounded">
        <thead className="bg-mcia_orange text-white hover:text-black">
          <tr>
            <th className="px-2 py-1 text-left">Prenotazione Giorno</th>
            <th className="px-2 py-1 text-left">Prenotazione Ora</th>
            <th className="px-2 py-1 text-left">Posti</th>
            <th className="px-2 py-1 text-left">Commissione MyCIA</th>
            <th className="px-2 py-1 text-center">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="border-t odd:bg-white even:bg-gray-200">
              <td className="px-2 py-1">{t.reserved_at}</td>
              <td className="px-2 py-1">{t.reserved_time}</td>
              <td className="px-2 py-1">{t.seats}</td>
              <td className="px-2 py-1">€ {t.mycia_commission.toFixed(2)}</td>
              <td className="px-2 py-1">
                <div className="flex justify-center items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleUpdateStatusClick(t, 17)}
                    className={`text-orange-600 hover:text-orange-800 transition-colors ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="no show"
                    disabled={isUpdating}
                  >
                    <LiaUserSlashSolid size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdateStatusClick(t, 9)}
                    className={`text-red-600 hover:text-red-800 transition-colors ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Rifiutata dal cliente"
                    disabled={isUpdating}
                  >
                    <IoTrashOutline size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {confirmModal.isOpen && confirmModal.booking && (
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          title="Conferma eliminazione"
          message={
            <div className="space-y-2">
              <p>
                {confirmModal.status === 17
                  ? "Sei sicuro di voler segnare questa prenotazione come no show?"
                  : "Sei sicuro di voler segnare questa prenotazione come rifiutata dal cliente?"}
              </p>
              <div className="bg-gray-100 p-2 rounded text-xs border border-gray-200">
                <p><strong>Giorno:</strong> {confirmModal.booking.reserved_at}</p>
                <p><strong>Ora:</strong> {confirmModal.booking.reserved_time}</p>
                <p><strong>Posti:</strong> {confirmModal.booking.seats}</p>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default BookingDetailTable;
